/** The menu on every column header: sort, filter this column, group by, hide, pin left.
 *
 * A chevron appears on hover and on focus; **Alt+Down** on a focused header opens it (the grid owns that
 * key, this is only the menu). An item is drawn only when it can do something: no "Sort" on a column
 * the page cannot order, no "Filter this column" without a filter of that id, no "Group by" without a
 * plain-text value, no "Hide" on the column that names the row. A control that predictably does
 * nothing is a defect (design rule 6).
 */

import { CaretDown } from "@phosphor-icons/react";

import { PopMenu } from "./PopMenu";
import styles from "./tablePower.module.css";

export interface ColumnMenuProps {
  header: string;
  sortable: boolean;
  /** This column's place in the sort: its direction, or null when it is not sorted. */
  sorted: "asc" | "desc" | null;
  canFilter: boolean;
  canGroup: boolean;
  grouped: boolean;
  canHide: boolean;
  /** Not the first column: that one is always pinned. */
  canPin: boolean;
  pinned: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSort: (direction: "asc" | "desc") => void;
  onClearSort: () => void;
  onFilter: () => void;
  onGroup: () => void;
  onHide: () => void;
  onPin: () => void;
}

export function ColumnMenu(props: ColumnMenuProps) {
  const item = (label: string, run: () => void, close: (focus?: boolean) => void, testId?: string) => (
    <button
      key={label}
      type="button"
      role="menuitem"
      className={styles.item}
      data-testid={testId}
      onClick={() => {
        run();
        close(false);
      }}
    >
      <span className={styles.itemLabel}>{label}</span>
    </button>
  );
  const hasAny =
    props.sortable || props.canFilter || props.canGroup || props.canHide || props.canPin;
  if (!hasAny) return null;
  return (
    <PopMenu
      label={`${props.header} column menu`}
      open={props.open}
      onOpenChange={props.onOpenChange}
      align="right"
      columnMenu
      triggerClassName={styles.colTrigger}
      trigger={<CaretDown size={11} weight="bold" aria-hidden="true" />}
      testId={`column-menu-${props.header}`}
    >
      {(close) => (
        <>
          {props.sortable ? item("Sort A to Z", () => props.onSort("asc"), close, "menu-sort-asc") : null}
          {props.sortable ? item("Sort Z to A", () => props.onSort("desc"), close, "menu-sort-desc") : null}
          {props.sorted ? item("Clear sort", props.onClearSort, close, "menu-sort-clear") : null}
          {props.canFilter ? item("Filter this column", props.onFilter, close, "menu-filter") : null}
          {props.canGroup
            ? item(props.grouped ? "Remove grouping" : "Group by this column", props.onGroup, close, "menu-group")
            : null}
          {props.canPin ? item(props.pinned ? "Unpin" : "Pin left", props.onPin, close, "menu-pin") : null}
          {props.canHide ? item("Hide column", props.onHide, close, "menu-hide") : null}
        </>
      )}
    </PopMenu>
  );
}
